const BLOCK_TAGS = new Set([
  "P",
  "UL",
  "OL",
  "LI",
  "BLOCKQUOTE",
  "H1", "H2", "H3", "H4", "H5", "H6",
]);

const INLINE_TAGS = new Set([
  "B", "I", "U", "STRONG", "EM", "SPAN", "A", "STRIKE", "S"
]);

export function normalizeDocument(editorRef) {
  const root = editorRef?.root;
  if (!root) return;

  // ========================
  // 1. Nếu editor rỗng
  // ========================
  if (isRootEmpty(root)) {
    root.innerHTML = "";
    const p = createEmptyP();
    root.appendChild(p);
    placeCaret(p);
    return;
  }

  normalizeBrNode(root)

  removeEmptyParagraph(root)

  convertDivToP(root);

  wrapRootOrphanNodes(root);

  removeConsecutiveBr(root);

  removeEmptyTags(root);

  ensureRootHasBlock(root);
}

function isRootEmpty(root) {
  return (
    root.children.length === 0 ||
    (root.children.length === 1 && !root.textContent?.trim())
  );
}

function createEmptyP() {
  const p = document.createElement("p");
  p.innerHTML = "<br>";
  return p;
}

function placeCaret(node) {
  const range = document.createRange();
  const sel = window.getSelection();
  range.setStart(node, 0);
  range.collapse(true);
  sel?.removeAllRanges();
  sel?.addRange(range);
}

function convertDivToP(root) {
  const divs = root.querySelectorAll("div");

  divs.forEach((div) => {
    // bỏ qua root
    if (div === root) return;

    // bỏ qua indent wrapper
    if (div.hasAttribute("data-indent")) return;

    const p = document.createElement("p");

    while (div.firstChild) {
      p.appendChild(div.firstChild);
    }

    div.replaceWith(p);
  });
}

function wrapRootOrphanNodes(root) {
  const nodes = Array.from(root.childNodes);
  let buffer = [];

  const flush = () => {
    if (buffer.length === 0) return;

    const p = document.createElement("p");

    // Move nodes to p
    buffer.forEach((n) => {
      // Check if node is still in the DOM and is a child of root
      if (n.parentNode === root) {
        p.appendChild(n);
      }
    });

    // Only insert if p has content
    if (p.childNodes.length > 0) {
      // Find a valid reference node that's still in root
      let referenceNode = null;
      for (const node of buffer) {
        if (node.parentNode === root) {
          referenceNode = node;
          break;
        }
      }

      if (referenceNode) {
        root.insertBefore(p, referenceNode);
      } else {
        root.appendChild(p);
      }
    }

    buffer = [];
  };

  nodes.forEach((node) => {
    // text node
    if (
      node.nodeType === Node.TEXT_NODE &&
      node.textContent?.trim()
    ) {
      buffer.push(node);
      return;
    }

    // inline element
    if (
      node.nodeType === Node.ELEMENT_NODE &&
      !BLOCK_TAGS.has((node).tagName) &&
      !(node).hasAttribute?.("data-indent")
    ) {
      buffer.push(node);
      return;
    }

    flush();
  });

  flush();
}

function ensureRootHasBlock(root) {
  const hasBlock = Array.from(root.children).some((el) =>
    BLOCK_TAGS.has(el.tagName)
  );

  if (!hasBlock) {
    const p = createEmptyP();
    while (root.firstChild) {
      p.appendChild(root.firstChild);
    }
    root.appendChild(p);
  }
}

function removeConsecutiveBr(root) {
  // Find all <br> tags
  const brTags = Array.from(root.querySelectorAll("br"));

  brTags.forEach((br) => {
    let nextSibling = br.nextSibling;

    // Remove consecutive <br> tags
    while (nextSibling) {
      if (nextSibling.nodeType === Node.ELEMENT_NODE &&
        nextSibling.tagName === "BR") {
        const toRemove = nextSibling;
        nextSibling = nextSibling.nextSibling;
        toRemove.remove();
      } else if (nextSibling.nodeType === Node.TEXT_NODE &&
        !nextSibling.textContent.trim()) {
        // Skip empty text nodes
        nextSibling = nextSibling.nextSibling;
      } else {
        break;
      }
    }
  });
}

function removeEmptyTags(root) {
  // Remove empty inline tags recursively
  let changed = true;
  let iterations = 0;
  const maxIterations = 10; // Prevent infinite loops

  while (changed && iterations < maxIterations) {
    changed = false;
    iterations++;

    INLINE_TAGS.forEach((tag) => {
      root.querySelectorAll(tag).forEach((el) => {
        // Don't remove span with style attributes (e.g., color)
        if (tag === "SPAN" && el.hasAttribute("style")) {
          return;
        }

        // Don't remove links with href
        if (tag === "A" && el.hasAttribute("href")) {
          return;
        }

        // Don't remove indent wrappers
        if (el.hasAttribute("data-indent")) {
          return;
        }

        // Check if element is truly empty (no text, no meaningful children)
        const hasText = el.textContent?.trim();
        const hasImage = el.querySelector("img");

        // Special case: if element only contains <br>, it's considered empty
        const onlyBr = !hasText && el.querySelector("br") &&
          el.childNodes.length === 1 &&
          el.firstChild.tagName === "BR";

        if ((!hasText && !hasImage) || onlyBr) {
          // If element only has <br>, move it outside before removing the wrapper
          if (onlyBr && el.parentNode) {
            el.parentNode.insertBefore(el.firstChild, el);
          }
          el.remove();
          changed = true;
        }
      });
    });
  }
}

export const normalizeParagraph = (root) => {
  if (!root || !root.querySelectorAll) return

  const BLOCK_TAGS = [
    'p',
    'blockquote',
    'pre',
    'li',
    'h1',
    'h2',
    'h3',
    'h4',
    'h5',
    'h6'
  ]

  const blocks = root.querySelectorAll(BLOCK_TAGS.join(','))

  blocks.forEach((el) => {
    const html = el.innerHTML
      .replace(/<br\s*\/?>/gi, '')
      .trim()

    if (html === '') {
      el.innerHTML = '<br>'
    }
  })
}

export const normalizeBrNode = (root) => {
  if (!root || typeof root.querySelectorAll !== 'function') return

  const brNodes = root.querySelectorAll(':scope > br')

  brNodes.forEach((br) => {
    const p = document.createElement('p')
    const newBr = document.createElement('br')

    p.appendChild(newBr)

    br.replaceWith(p)
  })
}

export const removeEmptyParagraph = (root) => {
  if (!(root instanceof Element)) return

  const paragraphs = [...root.children].filter(node => node.tagName === 'P')

  paragraphs.forEach((p) => {
    // chỉ remove <p></p>
    if (p.childNodes.length === 0) {
      p.remove()
    }
  })
}